// Pure pagination math for the bloodwork-analysis PDF.
//
// The PDF renders a sequence of bar-graph sections. Sections of varying
// heights are packed onto fixed-capacity pages: each subsequent section
// either fits onto the current page or starts a new one. When the WBC
// section is visible it always gets its own page between a designated
// pre-WBC barrier section (currently INFLAMMATION) and the section that
// follows it (currently PLATELETS), so the packer resets page usage when
// it crosses that barrier.

export type PackUnit = {
  id: string;
  height: number;
  visible: boolean;
};

export type PackingResult = {
  // Map of unit id -> whether a forced page break should follow it.
  unitBreakAfter: Record<string, boolean>;
  // Height used on the last page after the final unit is placed. Used by
  // callers to decide whether downstream content (Insights, etc.) fits on
  // the same page or needs its own.
  pageUsed: number;
};

export type PackingInput = {
  packUnits: PackUnit[];
  wbcIntervenes: boolean;
  // The packUnit id that is immediately before the WBC barrier in the
  // visual order. When wbcIntervenes is true, the first visible unit
  // whose position is after this id is treated as starting a fresh page.
  preWbcBarrierId: string;
  // packUnit ids that are forced onto their own fresh page by the JSX
  // (typically via an empty <div className="html2pdf__page-break" />
  // sibling). The packer treats each of these as a page start so that
  // pageUsed correctly resets to the unit's height — otherwise subsequent
  // sections see an inflated pageUsed value and get pushed onto their own
  // pages even when they would fit alongside the forced-break section.
  forcedPageStartIds?: string[];
  pageCapacity: number;
  sectionGap: number;
};

export function computePdfSectionPacking(input: PackingInput): PackingResult {
  const {
    packUnits,
    wbcIntervenes,
    preWbcBarrierId,
    forcedPageStartIds,
    pageCapacity,
    sectionGap,
  } = input;
  const visibleUnits = packUnits.filter(u => u.visible);
  const unitBreakAfter: Record<string, boolean> = {};
  visibleUnits.forEach(u => { unitBreakAfter[u.id] = false; });

  const barrierPackIdx = packUnits.findIndex(u => u.id === preWbcBarrierId);
  const wbcBarrierIdx = wbcIntervenes && barrierPackIdx >= 0
    ? visibleUnits.findIndex(u =>
        packUnits.findIndex(p => p.id === u.id) > barrierPackIdx,
      )
    : -1;

  const forcedPageStartSet = new Set(forcedPageStartIds ?? []);

  let pageUsed = 0;
  for (let i = 0; i < visibleUnits.length; i++) {
    const unit = visibleUnits[i];
    const isPageStart =
      i === 0 || i === wbcBarrierIdx || forcedPageStartSet.has(unit.id);
    if (isPageStart) {
      pageUsed = unit.height;
    } else if (pageUsed + sectionGap + unit.height > pageCapacity) {
      unitBreakAfter[visibleUnits[i - 1].id] = true;
      pageUsed = unit.height;
    } else {
      pageUsed += sectionGap + unit.height;
    }
  }

  return { unitBreakAfter, pageUsed };
}
